<template>
  <b-row class="my-3">
    <b-col cols="12" sm="6">
      <h4><b>Participants Filter</b></h4>
      <p>Select participants by department and/or location</p>
      <advanced-form :form="formFields" v-bind="form" v-model="form" :hideFormControl="true" />
      <hr />
      <b-alert class="mb-3" show :variant="totalInSelection ? 'outline-primary' : 'outline-secondary'">
        <strong>{{ totalInSelection | plural('participant') }} are in selection</strong>
      </b-alert>
      <p>When a new employee joins organisation that fulfils the requirements above they will be automatically added to the campaign.</p>
    </b-col>
    <b-col cols="12" sm="6" class="border-left">
      <div>
        <b-input v-model="searchTerm" placeholder="Search Employee..." />
      </div>
      <hr class="d-sm-none"/>
      <b-list-group v-if="Array.isArray(participants) && participants.length" class="my-3">
        <b-list-group-item
          v-for="({ entry }) in participants"
          :key="entry.id"
          id="participants-list"
          :style="{
            'background-color': notIDs.includes(entry.id) ? '#f3f4f6' : 'white',
            'color': notIDs.includes(entry.id) ? '#6b7280' : 'black',
          }"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span>{{ entry.firstName }} {{ entry.lastName }} ({{ entry.emailAddress }})</span>

            <b-button
              size="sm"
              variant="outline-primary"
              @click="toggleExcludeParticipant(entry.id)"
            >{{ notIDs.includes(entry.id) ? 'Include' : 'Exclude' }}</b-button>
          </div>
        </b-list-group-item>
      </b-list-group>

      <div v-else class="p-4 text-center">No Employees Found</div>

      <!-- Pagination -->
      <div class="d-flex justify-content-center">
        <b-pagination v-model="page" :total-rows="total" :per-page="limit" aria-controls="participants-list" />
      </div>
    </b-col>
    <b-col class="d-flex flex-row justify-content-start align-items-center" cols="12">
      <b-btn @click="back" variant="transparent">Back</b-btn>
      <div class="flex-fill"/>
      <b-btn class="mr-2" @click="saveSegment" variant="outline-primary">Save</b-btn>
    </b-col>
  </b-row>
</template>

<script>
import _isEqual from 'lodash/isEqual';
import CampaignAdminScimUsersSegmentQuery from '@/graphql/queries/CampaignAdminScimUsersSegment.gql';
import AdvancedForm from '@src/components/AdvancedForm';
import { mapRoot } from '@src/mixins/root';

export default {
  name: 'CampaignAdminScimUsersSegment',
  props: {
    campaign: {
      type: Object,
    },
    value: {
      type: Object,
    }
  },
  components: {
    AdvancedForm
  },
  data: () => ({
    searchTerm: null,
    page: 1,
    limit: 10,
    form: {},
    notIDs: [],
    initialReturnSegment: null
  }),
  apollo: {
    api: {
      query: CampaignAdminScimUsersSegmentQuery,
      variables() {
        const { segment, searchTerm, limit, page } = this;
        const whereScimUsers = {
          ...(segment || {}),
          ...(searchTerm && searchTerm.length > 1 ? { OR: [ { name: searchTerm }, { emailAddressLike: searchTerm } ] } : {}),
          active: true,
        };
        return { whereScimUsers, limit, page };
      },
      update({ company }) {
        return company;
      },
      fetchPolicy: 'network-only'
    },
    totalInSelection: {
      query: CampaignAdminScimUsersSegmentQuery,
      variables() {
        const { returnSegment } = this;
        const whereScimUsers = { ...(returnSegment || {}), active: true };
        return { whereScimUsers, limit: 1, page: 1 };
      },
      update({ company }) {
        const { scimUserConnections } = company || {};
        const { total } = scimUserConnections || {};
        return total || 0;
      },
      fetchPolicy: 'network-only'
    },
  },
  created() {
    // Set segment form
    const { value } = this;
    if (!value) return;
    const { AND } = value;
    if (!Array.isArray(AND) || !AND.length) return;
    this.form = AND.reduce((r, { NOT, OR }) => {
      if (NOT && NOT.length) {
        const { id_in: idIn } = NOT[0];
        this.notIDs = idIn;
        return r;
      } else if (Array.isArray(OR) && OR.length) {
        if (OR[0].location) {
          r.locations = OR.map(({ location }) => location).filter(el => el);
          return r;
        } else if (OR[0].department) {
          r.departments = OR.map(({ department }) => department).filter(el => el);
          return r;
        } else if (OR[0].organization) {
          r.organizations = OR.map(({ organization }) => organization).filter(el => el);
          return r;
        } else if (OR[0].division) {
          r.divisions = OR.map(({ division }) => division).filter(el => el);
          return r;
        } else if (OR[0].title) {
          r.titles = OR.map(({ title }) => title).filter(el => el);
          return r;
        } else if (OR[0].countryCode) {
          r.countryCodes = OR.map(({ countryCode }) => countryCode).filter(el => el);
          return r;
        }
      }
      return r;
    }, {});

    setTimeout(() => {
      this.initialReturnSegment = JSON.parse(JSON.stringify(this.returnSegment || {}));
    }, 500);
  },
  computed: {
    ...mapRoot([ 'company', 'user', 'isRecruiter', 'isRecruiterAdmin', 'isProd', 'isEXPD8', 'isChalhoub', 'isCapco', 'isInMobi', 'isERM', 'isCelonis', 'isBallastNedam', 'isAssurant', 'isOLX', 'isApplusIdiada', 'isBT', 'isGROWMARK' ]),
    unsavedChanges() {
      return !_isEqual(this.initialReturnSegment, this.returnSegment);
    },
    participants() {
      const { scimUserConnections } = this.api || {};
      const { edges } = scimUserConnections || {};
      return edges || [];
    },
    total() {
      const { scimUserConnections } = this.api || {};
      return scimUserConnections && scimUserConnections.total ? scimUserConnections.total : 0;
    },
    segment() {
      const { form } = this;
      const { departments, locations, organizations, divisions, titles, countryCodes } = form || {};

      if ((!Array.isArray(departments) || !departments.length) &&
        (!Array.isArray(locations) || !locations.length) &&
        (!Array.isArray(organizations) || !organizations.length) &&
        (!Array.isArray(divisions) || !divisions.length) &&
        (!Array.isArray(titles) || !titles.length) &&
        (!Array.isArray(countryCodes) || !countryCodes.length)) {
        return null;
      }

      const AND = [ { active: true } ];
      if (Array.isArray(departments) && departments.length) {
        AND.push({ OR: departments.map(value => ({ department: value })) });
      }
      if (Array.isArray(locations) && locations.length) {
        AND.push({ OR: locations.map(value => ({ location: value })) });
      }
      if (Array.isArray(organizations) && organizations.length) {
        AND.push({ OR: organizations.map(value => ({ organization: value })) });
      }
      if (Array.isArray(divisions) && divisions.length) {
        AND.push({ OR: divisions.map(value => ({ division: value })) });
      }
      if (Array.isArray(titles) && titles.length) {
        AND.push({ OR: titles.map(value => ({ title: value })) });
      }
      if (Array.isArray(countryCodes) && countryCodes.length) {
        AND.push({ OR: countryCodes.map(value => ({ countryCode: value })) });
      }

      return { AND };
    },
    returnSegment() {
      const { segment, notIDs } = this;
      if (!segment && !(Array.isArray(notIDs) || notIDs.length)) return null;
      const result = JSON.parse(JSON.stringify({ ...segment }));
      if (notIDs.length && Array.isArray(result.AND)) {
        result.AND.push({ NOT: [ { id_in: notIDs } ] });
      } else if (notIDs.length) {
        result.AND = { NOT: [ { id_in: notIDs } ] };
      }
      return result;
    },
    formFields() {
      const { scimUserAggs } = this.api || {};
      const { departments, locations, organizations, divisions, countryCodes, titles } = scimUserAggs || {};
      return {
        ...((this.isApplusIdiada) && Array.isArray(organizations) && organizations.length ? {
          organizations: {
            label: 'Companies',
            type: 'MULTISELECT',
            choices: organizations.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...((this.isApplusIdiada) && Array.isArray(divisions) && divisions.length ? {
          divisions: {
            label: 'Areas',
            type: 'MULTISELECT',
            choices: divisions.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(!this.isInMobi && !this.isERM && Array.isArray(departments) && departments.length ? {
          departments: {
            label: 'Departments',
            type: 'MULTISELECT',
            choices: departments.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isGROWMARK && Array.isArray(divisions) && divisions.length ? {
          divisions: {
            label: 'Cost Centers',
            type: 'MULTISELECT',
            choices: divisions.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(!this.isInMobi && !this.isERM && Array.isArray(locations) && locations.length ? {
          locations: {
            label: 'Locations',
            type: 'MULTISELECT',
            choices: locations.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isEXPD8 && Array.isArray(organizations) && organizations.length ? {
          organizations: {
            label: 'Regions',
            type: 'MULTISELECT',
            choices: organizations.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isEXPD8 && Array.isArray(divisions) && divisions.length ? {
          divisions: {
            label: 'Districts',
            type: 'MULTISELECT',
            choices: divisions.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isInMobi && Array.isArray(departments) && departments.length ? {
          departments: {
            label: 'Cost Owners',
            type: 'MULTISELECT',
            choices: departments.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isInMobi && Array.isArray(divisions) && divisions.length ? {
          divisions: {
            label: 'Functions',
            type: 'MULTISELECT',
            choices: divisions.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isInMobi && Array.isArray(locations) && locations.length ? {
          locations: {
            label: 'Cities',
            type: 'MULTISELECT',
            choices: locations.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...((this.isChalhoub || this.isInMobi || this.isERM || this.isCelonis || this.isAssurant || this.isOLX) && Array.isArray(countryCodes) && countryCodes.length ? {
          countryCodes: {
            label: 'Countries',
            type: 'MULTISELECT',
            choices: countryCodes.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isERM && Array.isArray(locations) && locations.length ? {
          locations: {
            label: 'Locations',
            type: 'MULTISELECT',
            choices: locations.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...((this.isERM || this.isInMobi || this.isBallastNedam) && Array.isArray(organizations) && organizations.length ? {
          organizations: {
            label: 'Organizations',
            type: 'MULTISELECT',
            choices: organizations.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isERM && Array.isArray(departments) && departments.length ? {
          departments: {
            label: 'Departments',
            type: 'MULTISELECT',
            choices: departments.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isERM && Array.isArray(divisions) && divisions.length ? {
          divisions: {
            label: 'Managers',
            type: 'MULTISELECT',
            choices: divisions.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...((this.isERM || this.isCelonis || this.isInMobi || this.isGROWMARK) && Array.isArray(titles) && titles.length ? {
          titles: {
            label: 'Titles',
            type: 'MULTISELECT',
            choices: titles.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isCapco && Array.isArray(organizations) && organizations.length ? {
          organizations: {
            label: 'Capabilities',
            type: 'MULTISELECT',
            choices: organizations.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isCapco && Array.isArray(divisions) && divisions.length ? {
          divisions: {
            label: 'Domains',
            type: 'MULTISELECT',
            choices: divisions.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isBT && Array.isArray(divisions) && divisions.length ? {
          divisions: {
            label: 'Cities',
            type: 'MULTISELECT',
            choices: divisions.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...((this.isBT) && Array.isArray(countryCodes) && countryCodes.length ? {
          countryCodes: {
            label: 'Countries',
            type: 'MULTISELECT',
            choices: countryCodes.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
      };
    }
  },
  methods: {
    toggleExcludeParticipant(scimUserID) {
      const { notIDs } = this;
      if (notIDs.includes(scimUserID)) {
        this.notIDs = notIDs.filter(el => el !== scimUserID);
      } else {
        this.notIDs = [ ...notIDs, scimUserID ];
      }
    },
    saveSegment() {
      const { returnSegment } = this;
      this.initialReturnSegment = JSON.parse(JSON.stringify(this.returnSegment || {}));
      this.$emit('saveSegment', returnSegment);
    },
    back() {
      this.$emit('back');
    },
  },
};
</script>
<style lang="scss" scoped>
  .inactiveButton {
    color: #001DC0;
    text-decoration: underline;
  }
  .activeButton {
    color: white;
    background-color: #001DC0;
  }
  .activeButton:hover {
    background-color: red;
    color: white;
  }
  .inactiveButton:hover {
    color: white;
    background-color: #001DC0;
  }
  .hidden {
    visibility: hidden;
  }
  .link-styling {
    text-decoration: underline;
    cursor: pointer;
  }
</style>



// WEBPACK FOOTER //
// src/components/campaign-admin/update-scim-user-segment.vue